import React, { useEffect, useState, useMemo } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Select from "react-select";
import Button from "react-bootstrap/Button";
import { toast } from "react-toastify";
import { getTodayFormatted } from "../../../utils/date";
import { useClients } from "../../../hooks/useGetClients";
import { useUsers } from "../../../hooks/useGetUsers";
import { useGroupes } from "../../../hooks/useGetGroupes";
import { useCreateProjet } from "../../../hooks/PROJET/useCreateProjet";
import { addProjetUtilisateurs, addProjetGroupes } from "../../../api/PROJETS/projets";
import { customSelectStyles } from "../../../utils/reactSelectStyles";
import MarkdownEditor from "../../MarkdownEditor/MarkdownEditor";
import { useAuth } from "../../../context/AuthContext";

const toApiDate = (dateStr) => (dateStr ? `${dateStr} 00:00:00` : null);

const NewProject = ({ showNewProject, setShowNewProject, defaultClient = null }) => {
  const { user } = useAuth();

  const [titre, setTitre]               = useState("");
  const [description, setDescription]   = useState("");
  const [dateDebut, setDateDebut]       = useState("");
  const [dateFin, setDateFin]           = useState("");
  const [checkDateFin, setCheckDateFin] = useState(false);

  const [selectedClient, setSelectedClient] = useState(
    defaultClient ? { value: defaultClient.id, label: defaultClient.name } : null
  );
  const [selectedCollaborateurs, setSelectedCollaborateurs] = useState([]);
  const [selectedGroupes, setSelectedGroupes] = useState([]);

  const [clientSearch, setClientSearch] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createMutation = useCreateProjet();
  const { data: clientsData } = useClients(1, 10, clientSearch);
  const { data: usersData } = useUsers();
  const { data: groupesData } = useGroupes();

  const clientOptions = (clientsData?.data?.clients ?? []).map((c) => ({
    value: c.id, label: c.name,
  }));

  const optionsCollaborateurs = useMemo(() =>
    usersData?.data?.map(u => ({
      value: u.id,
      label: `${u.surname || ""} ${u.name || ""}`.trim()
    })) || [], [usersData]
  );

  const optionsGroupes = useMemo(() =>
    groupesData?.data?.map(g => ({
      value: g.id,
      label: g.name,
    })) || [], [groupesData]
  );

  const isClientSearchTooShort = clientSearch.trim().length > 0 && clientSearch.trim().length < 3;

  // Initialisation à l'ouverture de la modale
  useEffect(() => {
    if (!showNewProject) return;

    setDateDebut(getTodayFormatted());

    // Pré-sélectionner l'utilisateur courant comme collaborateur
    if (selectedCollaborateurs.length === 0 && user?.data?.id && optionsCollaborateurs.length > 0) {
      const me = optionsCollaborateurs.find(u => u.value === user.data.id);
      if (me) setSelectedCollaborateurs([me]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNewProject, optionsCollaborateurs, user]);

  const resetForm = () => {
    setTitre("");
    setDescription("");
    setDateDebut(getTodayFormatted());
    setDateFin("");
    setCheckDateFin(false);
    setSelectedClient(defaultClient ? { value: defaultClient.id, label: defaultClient.name } : null);
    setSelectedCollaborateurs([]);
    setSelectedGroupes([]);
    setClientSearch("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedClient) return toast.error("Veuillez sélectionner un client");
    if (!titre.trim())   return toast.error("Veuillez saisir un titre");
    if (!dateDebut)      return toast.error("Veuillez saisir une date de début");

    const payload = {
      projet: {
        titre,
        client: selectedClient.value,
        statut: "open",
        date_debut: toApiDate(dateDebut),
        date_fin:   checkDateFin && dateFin ? toApiDate(dateFin) : null,
        description: description || null,
      },
    };

    setIsSubmitting(true);
    try {
      const result = await createMutation.mutateAsync(payload);
      const newProjectId = result.data.id;

      if (selectedCollaborateurs.length > 0) {
        const userIds = selectedCollaborateurs.map(opt => opt.value);
        await addProjetUtilisateurs(newProjectId, userIds);
      }

      if (selectedGroupes.length > 0) {
        const groupeIds = selectedGroupes.map(opt => opt.value);
        await addProjetGroupes(newProjectId, groupeIds);
      }

      toast.success("Projet créé avec succès !");
      resetForm();
      setShowNewProject(false);
    } catch (err) {
      toast.error(`Erreur lors de la création : ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={showNewProject} onHide={() => { resetForm(); setShowNewProject(false); }} size="lg">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Création d'un projet</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="mb-3">
            <Form.Group as={Col} md={12}>
              <Form.Label>Client *</Form.Label>
              <Select
                options={isClientSearchTooShort ? [] : clientOptions}
                value={selectedClient}
                onChange={setSelectedClient}
                onInputChange={setClientSearch}
                inputValue={clientSearch}
                filterOption={() => true}
                placeholder="Chercher un client..."
                isClearable
                styles={customSelectStyles}
                noOptionsMessage={() =>
                  isClientSearchTooShort ? "Minimum 3 caractères" : "Aucun client trouvé"
                }
              />
            </Form.Group>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Titre *</Form.Label>
            <Form.Control
              type="text"
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              placeholder="Ex : Migration infrastructure serveur..."
            />
          </Form.Group>

          <Row className="mb-3">
            <Form.Group as={Col} md={6}>
              <Form.Label>Groupes assignés</Form.Label>
              <Select
                isMulti
                options={optionsGroupes}
                value={selectedGroupes}
                onChange={setSelectedGroupes}
                placeholder="Sélectionner..."
                styles={customSelectStyles}
                closeMenuOnSelect={false}
              />
            </Form.Group>
            <Form.Group as={Col} md={6}>
              <Form.Label>Intervenants assignés</Form.Label>
              <Select
                isMulti
                options={optionsCollaborateurs}
                value={selectedCollaborateurs}
                onChange={setSelectedCollaborateurs}
                placeholder="Sélectionner..."
                styles={customSelectStyles}
                closeMenuOnSelect={false}
              />
            </Form.Group>
          </Row>

          <Row className="mb-2">
            <Form.Group as={Col} md={6}>
              <Form.Label>Date de début *</Form.Label>
              <Form.Control
                type="date"
                value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)}
              />
            </Form.Group>
          </Row>

          <div className="mb-3">
            <Form.Check
              type="checkbox"
              id="check-date-fin-projet"
              label="Définir une date de fin"
              checked={checkDateFin}
              onChange={(e) => setCheckDateFin(e.target.checked)}
              className="text-muted"
              style={{ fontSize: "0.875rem" }}
            />
            {checkDateFin && (
              <Row className="mt-2">
                <Form.Group as={Col} md={6}>
                  <Form.Control
                    type="date"
                    value={dateFin}
                    onChange={(e) => setDateFin(e.target.value)}
                  />
                </Form.Group>
              </Row>
            )}
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <MarkdownEditor
              value={description}
              onChange={setDescription}
              rows={3}
              placeholder="Objectifs, périmètre, contexte…"
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" disabled={isSubmitting} onClick={() => { resetForm(); setShowNewProject(false); }}>
            Annuler
          </Button>
          <Button variant="info" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Chargement..." : "Créer"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default NewProject;